// diese Signatur beschreibt im Prinzip jede denkbare Funktion - dient der Vereinfachung der Schreibweise von memoize
import { MultiMap } from "./MultiMap";

export type KeysMapper<Keys extends any[], V> = (...keys: Keys) => V;

export const UNDEFINED = Symbol("undefined") as any;

export const createLazy = <Keys extends any[], V>(
  map: MultiMap<Keys, V>,
  keys: Keys,
  fun: KeysMapper<Keys, V>,
): V => {
  let value = map.get(keys);
  if (value === undefined) {
    value = fun(...keys);
    if (value === undefined) {
      value = UNDEFINED;
    }
    map.set(keys, value);
  }
  if (value === UNDEFINED) {
    return undefined;
  }
  return value;
};

/**
 * ein spezifisches memoize, das nur für Funktionen mit fixer Anzahl von Parametern verwendet werden kann.
 */
export function memoizeFix<Keys extends any[], V>(fun: KeysMapper<Keys, V>): KeysMapper<Keys, V> {
  // MultiMap funktioniert nur, wenn keys immer dieselbe Länge hat (wird im ersten Aufruf festgelegt), daher "static keys length" im Namen.
  const map = new MultiMap<Keys, V>();
  return (...keys: Keys) => createLazy(map, keys, fun);
}

/**
 * cached eine beliebige Funktion mittels MultiMaps.
 * Ist ein universales memoize, das für Funktionen mit variabler Anzahl von Parametern verwendet werden kann.
 * Diese Strategie - mittels Multimap - ist neben Commons.cache, welches im Zweifel JSON.stringify verwendet -
 * eine der gebräuchlichsten Strategien zum Cachen.
 */
export function memoize<Keys extends any[], V>(fun: KeysMapper<Keys, V>): KeysMapper<Keys, V> {
  const funLengthMap = memoizeFix<[number], KeysMapper<Keys, V>>(() => memoizeFix<Keys, V>(fun));
  return (...keys: Keys) => funLengthMap(keys.length)(...keys);
}

/**
 * memoize ist so leicht zu verwechseln mit z.B. lodash
 */
export const memoizeUniversal = memoize;

const sourceKeyMapMap = new MultiMap<[string, number], MultiMap<any[], any>>();

const cacheStats = {
  cacheTry: 0,
  cacheMiss: 0,
  cacheMissSet: new Set(),
  callWithArrays: new Set(),
};

export const getCacheStats = () => cacheStats;

/**
 * Eine an React angelehnte Schreibweise, mit den Dependencies explizit als Parameter.
 * Im Gegensatz zu React wird mit dem Paramter sourceKey die Quelle des Caches identifiziert -
 * react macht das in Hooks mittels interner counter zur Runtime als Blackmagic.
 */
export const memoizeInplace = <Keys extends any[], V>(
  fun: () => V,
  sourceKey: string,
  dependencies: any[],
): V => {
  cacheStats.cacheTry++;
  const map = createLazy(
    sourceKeyMapMap,
    [sourceKey, dependencies.length],
    (sourceKey, dependenciesLength) => new MultiMap<Keys, V>(),
  );
  return createLazy<any[], V>(map, dependencies, () => {
    cacheStats.cacheMiss++;
    cacheStats.cacheMissSet.add(sourceKey);
    return fun();
  });
};

self["logCacheStats"] = () => {
  const { cacheTry, cacheMiss, cacheMissSet, callWithArrays } = cacheStats;
  // @ts-ignore
  console.log({
    cacheTry,
    cacheMiss,
    cacheHit: cacheTry - cacheMiss,
    cacheMissSet: Array.from(cacheMissSet),
    callWithArrays: Array.from(callWithArrays),
  });
  cacheStats.cacheTry = cacheStats.cacheMiss = 0;
  cacheMissSet.clear();
  callWithArrays.clear();
};
